import { isApiDomainError } from '@listening/api-contract/errors';

// Shared error-unwrapping for the effect-query layer (start / browser-ext / RN).
//
// `eq.queryOptions`/`eq.mutationOptions` wrap every queryFn/mutationFn failure
// in effect-query's `EffectQueryFailure` (an Error subclass) and stash the real
// typed error on `.failure`. Checking the raw error misses the typed failure
// underneath, so unwrap one level before inspecting it. Used by query error
// boundaries, router retry predicates, and callsite `onError` copy.
export function unwrapEffectQueryFailure(error: unknown): unknown {
  if (
    error !== null &&
    typeof error === 'object' &&
    '_tag' in error &&
    error._tag === 'EffectQueryFailure' &&
    'failure' in error
  ) {
    return error.failure;
  }
  return error;
}

// User-facing copy for a failed query/mutation, or `undefined` to fall back to
// a generic localized string. Unwraps the `EffectQueryFailure` envelope, then —
// if the underlying typed failure is one of the contract's domain errors
// (api-contract/errors.ts) — returns its backend-authored `message`. Transport
// errors (network/decode) and anything else return `undefined`.
export function effectQueryErrorMessage(error: unknown): string | undefined {
  const inner = unwrapEffectQueryFailure(error);
  return isApiDomainError(inner) ? inner.message : undefined;
}
